import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type EnrollmentStatus = 'Requested' | 'Started' | 'Completed';

export interface CampaignIdStatusMap {
  [key: string]: EnrollmentStatus;
}

export interface EnrolledCampaign {
  campaignId: string;
  completedOn: Date;
  siteId: string;
  startedOn: Date;
  status: EnrollmentStatus;
}

export interface Profile {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  photos?: {
    id: string;
    url: string;
  }[];
  photoUrls?: string[];
  ageConfirmed?: boolean;
  ageConfirmationTimestamp?: string;
  optInTimestamp?: string;
  optIn?: boolean;
  formId?: string;
  externalRequestId?: string;
  enrolledCampaigns?: EnrolledCampaign[];
}

export type GlobalStore = {
  isInit: boolean;
  profileId: string;
  loginEmail: string;
  showSiteLayout: boolean;
  footerHeight: number;
};

export type GlobalAction = {
  setIsInit: (isInit: boolean) => void;
  setShowSiteLayout: (showSiteLayout: boolean) => void;
  setFooterHeight: (height: number) => void;
  reset: () => void;
  setProfileId: (profileId: string) => void;
};

const initialState: GlobalStore = {
  isInit: true,
  profileId: '',
  loginEmail: '',
  showSiteLayout: true,
  footerHeight: 0
};

const useGlobalStore = create(
  persist<GlobalStore & GlobalAction>(
    (set) => ({
      ...initialState,
      setProfileId: (profileId: string) => set(() => ({ profileId })),
      setIsInit: (val: boolean) => set(() => ({ isInit: val })),
      setShowSiteLayout: (val: boolean) => set(() => ({ showSiteLayout: val })),
      setFooterHeight: (height: number) => set(() => ({ footerHeight: height })),
      reset: () => set(initialState),
    }),
    {
      name: 'global-store',
      version: 1, // <= bump this whenever your schema changes
      migrate: (persisted: any, fromVersion) => {
        if (fromVersion < 1 && persisted) {
          // drop the removed property
          const { token, ...rest } = persisted
          return rest
        }
        return persisted
      },
      // ensure future writes never include the removed key
      partialize: (state) => {
        const { token, ...rest } = state as any
        return rest
      },
    }
  )
);

export { useGlobalStore };
